import { ComponentType, Fragment } from 'react';

import styled, { CSSObject } from 'styled-components';

import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { PopoverButton, PopoverButtonProps } from '@components/popover-button';
import { Stack } from '@components/stack';
import { backgroundSlide } from '@styles/animation';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { AppliedFilter } from './applied-filter';
import { inlineComparers, inlineTypes, useFilterTranslate } from './filter-utils';
import { FilterListItem } from './list-item';
import { FilterListSeparator } from './list-separator';
import { BooleanMenu } from './menus/boolean-menu';
import { RELATIVE_COMPARERS, DateMenu } from './menus/date-menu';
import { EmailMenu } from './menus/email-menu';
import { IssuesMenu } from './menus/issues-menu';
import { JsonMenu } from './menus/json-menu';
import { DecimalMenu, NumberMenu } from './menus/number-menu';
import { ParagraphMenu } from './menus/paragraph-menu';
import { PhoneMenu } from './menus/phone-menu';
import { ProgressMenu } from './menus/progress-menu';
import { SelectMenu } from './menus/select-menu';
import { TextMenu } from './menus/text-menu';
import { MenuProps } from './menus/types';
import { UserMenu } from './menus/user-menu';
import {
  AppliedFilterData,
  FilterConfigType,
  FilterData,
  FilterState,
  FilterConfig,
  FilterConfigField,
  SelectableFilterConfigField,
  SeparatorConfig,
} from './shared-types';
import { CurrentFilterActions, CurrentFilterState, useCurrentFilter } from './use-current-filter';

import { FilterActions } from '../table-store';

export const Filters = ({
  state,
  actions,
  config,
  addFilterText,
  isLoading,
}: {
  state: FilterState;
  actions: FilterActions;
  addFilterText: string;
  config: FilterConfig;
  isLoading?: boolean;
}) => {
  if (!state.isOpen) return null;

  if (state.appliedFilters.length === 0) {
    return (
      <Filter
        actions={actions}
        config={config}
        buttonProps={{
          iconLeft: <IconAddPlus />,
          text: addFilterText,
          size: 'sm',
          variant: 'secondary',
        }}
      />
    );
  }

  return (
    <Stack orientation="horizontal" space={2} style={{ minWidth: 0 }}>
      {isLoading ? (
        <AnimatedFilterSkeleton />
      ) : (
        <AppliedFiltersList
          state={state}
          actions={actions}
          config={config}
          appliedFilters={state.appliedFilters as Array<AppliedFilterData>}
        />
      )}
    </Stack>
  );
};

const AppliedFiltersList = ({
  appliedFilters,
  state,
  actions,
  config,
}: {
  appliedFilters: Array<AppliedFilterData>;
  actions: FilterActions;
  state: FilterState;
  config: FilterConfig;
}) => {
  const { t } = useFilterTranslate();
  return (
    <Fragment>
      {appliedFilters.map((filter, index) => {
        const filterConfig = config.find((cfg) => cfg.id === filter.category);
        if (!filterConfig) return null;

        return (
          <Stack orientation="horizontal" space={2} key={'filter-' + filter.id} align="center">
            {index > 0 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={actions.switchConjunction}
                text={t(`core.${state.conjunction}`)}
                style={{ textTransform: 'capitalize' }}
              />
            )}
            <Stack space={2} orientation="horizontal" style={{ minWidth: 0 }}>
              <Filter
                actions={actions}
                config={config}
                popoverStyle={{ minWidth: 0, display: 'flex' }}
                appliedFilter={filter}
                buttonProps={{
                  variant: 'secondary',
                  size: 'sm',
                  children: (
                    <AppliedFilter
                      filter={filter}
                      config={filterConfig as SelectableFilterConfigField}
                      actions={actions}
                    />
                  ),
                }}
              />
              {index === appliedFilters.length - 1 && (
                <Filter
                  actions={actions}
                  config={config}
                  trigger={IconButton}
                  buttonProps={{ variant: 'secondary', icon: <IconAddPlus />, size: 'md' }}
                />
              )}
            </Stack>
          </Stack>
        );
      })}
    </Fragment>
  );
};

const getCurrentConfig = (category: FilterConfigField, fieldId?: string): FilterConfigField => {
  if (category.type === 'category' && fieldId) {
    const fieldConfig = category.fields.find((field) => field.id === fieldId);
    return fieldConfig || category;
  }
  return category;
};

type FilterListMenuProps = {
  onSelect(category: string): void;
  state: CurrentFilterState;
  actions: CurrentFilterActions;
  config: FilterConfig;
};

const MENU_COMPONENTS: Record<FilterConfigType, ComponentType<MenuProps> | null> = {
  date: DateMenu,
  datetime: DateMenu,
  progress: ProgressMenu,
  text: TextMenu,
  paragraph: ParagraphMenu,
  email: EmailMenu,
  phone_number: PhoneMenu,
  single_select: SelectMenu,
  multi_select: SelectMenu,
  user: UserMenu,
  boolean: BooleanMenu,
  number: NumberMenu,
  decimal: DecimalMenu,
  issues: IssuesMenu,
  category: null,
  separator: null,
  custom: null,
  quick_filters: null,
  json: JsonMenu,
};

export function FilterListMenu({ config, onSelect, state, actions }: FilterListMenuProps) {
  const { t } = useFilterTranslate();

  const handleCategorySelect = (categoryId: string) => {
    const category = config.find((cfg) => cfg.id === categoryId);
    if (!category || category.type === 'separator') return;

    onSelect(categoryId);

    // Auto-select comparer if there's only one allowed AND no quick filters
    const hasQuickFilters = 'quick' in category && category.quick && category.quick.length > 0;
    if (
      !hasQuickFilters &&
      'allowedComparers' in category &&
      category.allowedComparers &&
      category.allowedComparers.length === 1
    ) {
      actions.selectComparer(category.allowedComparers[0]);
    }
  };

  if (!state.category) {
    return (
      <Fragment>
        {config.map((cfg, index) => {
          if (cfg.type === 'separator') {
            return <FilterListSeparator key={index} />;
          }
          if (cfg.type === 'quick_filters') {
            return (
              <Fragment key={index}>
                {cfg.quick.map((quickFilter, quickIndex) => (
                  <FilterListItem
                    key={`quick-${quickIndex}`}
                    onClick={() => {
                      actions.applyFilter({
                        id: quickFilter.id,
                        category: quickFilter.apply.category!,
                        comparer: quickFilter.apply.comparer!,
                        value: quickFilter.apply.value!,
                      });
                    }}
                    icon={quickFilter.icon}
                    label={quickFilter.label}
                  />
                ))}
                <FilterListSeparator />
              </Fragment>
            );
          }
          return (
            <FilterListItem
              key={index}
              onClick={() => handleCategorySelect(cfg.id)}
              icon={'icon' in cfg ? cfg.icon : undefined}
              label={'label' in cfg ? cfg.label : ''}
            />
          );
        })}
      </Fragment>
    );
  }

  const category = config.find((cfg) => cfg.id === state.category);
  if (!category) return null;

  const currentConfig = getCurrentConfig(category, state.field);
  if (currentConfig.type === 'separator') return null;

  const renderHeader = () => {
    // Check if the current config has only one allowed comparer (auto-selected and not displayed)
    const hasOnlyOneComparer =
      'allowedComparers' in currentConfig &&
      currentConfig.allowedComparers &&
      currentConfig.allowedComparers.length === 1;

    const showComparer = state.comparer && !RELATIVE_COMPARERS.includes(state.comparer);
    return (
      <Fragment>
        <FilterListItem
          heading
          onClick={actions.unselectCategory}
          icon={<IconArrowLeft />}
          label={category.type !== 'separator' && 'label' in category ? category.label : ''}
        />
        {state.field && (
          <FilterListItem
            heading
            onClick={actions.unselectField}
            icon={<IconArrowLeft />}
            label={'label' in currentConfig ? currentConfig.label : ''}
          />
        )}
        {state.comparer && showComparer && (
          <>
            {!inlineTypes.includes(currentConfig.type) &&
              !inlineComparers.includes(state.comparer) &&
              !hasOnlyOneComparer && ( // Hide comparer header if it's auto-selected
                <FilterListItem
                  heading
                  onClick={actions.unselectComparer}
                  icon={<IconArrowLeft />}
                  label={t(`core.comparator.${state.comparer}`)}
                />
              )}
          </>
        )}
        <FilterListSeparator />
      </Fragment>
    );
  };

  const renderQuickFilters = () => {
    if (!state.value && !state.comparer && 'quick' in currentConfig && currentConfig.quick) {
      return (
        <Fragment>
          {currentConfig.quick.map((quickField, index) => (
            <FilterListItem
              key={index}
              label={quickField.label}
              onClick={() =>
                actions.applyFilter({
                  category: currentConfig.id,
                  ...quickField.apply,
                })
              }
              icon={quickField.icon}
            />
          ))}
          <FilterListSeparator />
        </Fragment>
      );
    }
    return null;
  };

  const handleFieldSelect = (fieldId: string) => {
    if (currentConfig.type !== 'category') return;

    const field = currentConfig.fields.find((f) => f.id === fieldId);
    if (!field || field.type === 'separator') return;

    actions.selectField(fieldId);

    // Auto-select comparer if there's only one allowed AND no quick filters
    const hasQuickFilters = 'quick' in field && field.quick && field.quick.length > 0;
    if (
      !hasQuickFilters &&
      'allowedComparers' in field &&
      field.allowedComparers &&
      field.allowedComparers.length === 1
    ) {
      actions.selectComparer(field.allowedComparers[0]);
    }
  };

  const renderCategoryFields = () => {
    if (currentConfig.type === 'category' && !state.field) {
      return currentConfig.fields.map((field, index) => {
        if (field.type === 'separator') {
          return <FilterListSeparator key={field.id + index} />;
        }
        return (
          <FilterListItem
            key={field.id}
            label={field.label}
            onClick={() => handleFieldSelect(field.id)}
            icon={field.icon}
          />
        );
      });
    }
    return null;
  };

  const renderTypeMenu = () => {
    if (currentConfig.type === 'custom' && 'customMenu' in currentConfig) {
      const CustomMenuComponent = currentConfig.customMenu;
      return <CustomMenuComponent state={state} actions={actions} config={currentConfig} />;
    }

    const MenuComponent = MENU_COMPONENTS[currentConfig.type];
    if (MenuComponent) {
      return <MenuComponent state={state} actions={actions} config={currentConfig} />;
    }
    return null;
  };

  return (
    <Fragment>
      {renderHeader()}
      {renderQuickFilters()}
      {renderCategoryFields()}
      {renderTypeMenu()}
    </Fragment>
  );
}

function FilterContent({
  initialData,
  closeMenu,
  config,
  actions: collectionActions,
}: {
  initialData?: FilterData;
  closeMenu(): void;
  config: FilterConfig;
  actions: FilterActions;
}) {
  const [state, actions] = useCurrentFilter({
    closeMenu,
    initialData,
    collectionActions,
  });

  return (
    <FilterListMenu
      config={config}
      onSelect={actions.selectCategory}
      state={state}
      actions={actions}
    />
  );
}

function Filter({
  buttonProps,
  appliedFilter,
  popoverStyle,
  trigger,
  config,
  actions,
}: {
  trigger?: PopoverButtonProps['trigger'];
  buttonProps: PopoverButtonProps['triggerProps'];
  appliedFilter?: FilterData;
  popoverStyle?: CSSObject;
  config: FilterConfig;
  actions: FilterActions;
}) {
  return (
    <PopoverButton
      trigger={trigger}
      triggerProps={{
        ...buttonProps,
        'data-testid': 'filter-button',
      }}
      contentStyle={{ maxHeight: 'none', maxWidth: '19.25rem' }}
      popoverStyle={popoverStyle}
    >
      {({ close, isOpen }) => (
        <FilterContent
          key={String(isOpen)}
          actions={actions}
          config={config}
          initialData={appliedFilter}
          closeMenu={close}
        />
      )}
    </PopoverButton>
  );
}

export const filterSeparator: SeparatorConfig = {
  id: 'separator',
  type: 'separator',
};

const FilterSkeleton = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: pxToRem(32),
  width: pxToRem(300),
  borderRadius: pxToRem(6),
  background: `linear-gradient(
    145deg,
    ${theme.color.uiTrans10} 25%,
    ${theme.color.uiTrans20} 50%,
    ${theme.color.uiTrans10} 75%
  )`,
  backgroundSize: '200% 100%',
});

const AnimatedFilterSkeleton = styled(FilterSkeleton)`
  animation: ${backgroundSlide} 2s infinite linear;
`;
